/**
 * Track → category mapping for the /vault filter pill bar.
 *
 * Six buckets. Elite Prompts card stays pinned regardless of filter.
 * Keeps every track discoverable without the giant flat scroll.
 *
 * Any track slug not listed here falls through to "Freelance & Growth" as the
 * safe default (rare — every current track is mapped).
 */
import { VAULT } from "./vaultStats";

export const CATEGORY_PILLS = [
  { key: "all", label: `All ${VAULT.tracks}`, short: "All" },
  { key: "faceless", label: "Faceless & Content", short: "Faceless" },
  { key: "services", label: "Services & Consulting", short: "Services" },
  { key: "subs", label: "Subscriptions", short: "Subs" },
  { key: "commerce", label: "E-commerce & Digital", short: "Commerce" },
  { key: "growth", label: "Freelance & Growth", short: "Growth" },
  { key: "systems", label: "Templates & Systems", short: "Systems" },
  { key: "operator", label: "Operator Skills", short: "Skills" },
  { key: "aibiz", label: "AI Business Implementation", short: "AI Business" },
];

// Slug → category-key. Keep this file the single source of truth.
export const TRACK_CATEGORY_MAP = {
  "ai-commerce-readiness-audit": "aibiz",
  "ai-opportunity-audit-implementation-planner": "aibiz",
  "ai-shopping-visibility-aco": "aibiz",
  "ai-employee-installer": "aibiz",
  "ai-business-stack-integration": "aibiz",
  "ai-voice-receptionist": "aibiz",
  "local-business-ai-visibility": "aibiz",
  "marketplace-ai-operations": "aibiz",
  "agent-rescue-oversight": "aibiz",
  "cart-recovery-retention": "aibiz",
  // Faceless & Content — content-first income, no on-camera face required.
  "youtube-automation": "faceless",
  "youtube-clipping-rights-cleared-shorts": "faceless",
  "stock-content": "faceless",
  "tiktok-cash-cow": "faceless",
  "virtual-influencer": "faceless",
  "podcasting-fasttrack": "faceless",
  "podcast-growth": "faceless",
  "ai-youtube-automation": "faceless",
  "ai-faceless-tiktok": "faceless",
  "voice-clone-podcast-ghosthost": "faceless",
  "faceless-tiktok-book-club": "faceless",
  "faceless-instagram-reels-farm": "faceless",

  // Services & Consulting — productised per-job or per-client work.
  "ugc-ad-studio": "services",
  "voice-agent-reseller": "services",
  "ghostwriting-agency": "services",
  "lead-gen-service": "services",
  "toolbox-talk-safety-briefing-course-packs": "services",
  "building-safety-act-golden-thread-micro-training": "services",
  "retrofit-heat-pump-homeowner-education-course-kits": "services",
  "trades-apprentice-new-starter-onboarding-course-studio": "services",
  "site-manager-digital-skills-micro-courses": "services",
  "new-build-snagging-inspection-training": "services",
  "construction-estimating-career-switch-bootcamp": "services",
  "holiday-let-licensing-safety-binder-service": "services",
  "farm-diversification-opportunity-reports": "services",
  "pub-cafe-quiet-night-event-programme-service": "services",
  "venue-access-statement-inclusion-audit-service": "services",
  "care-provider-recruitment-copy-candidate-journey-kits": "services",
  "community-council-minutes-public-notice-service": "services",
  "tutor-progress-report-parent-update-service": "services",
  "cafe-allergen-matrix-menu-compliance-service": "services",
  "local-business-online-presence-monitoring-subscription": "services",
  "local-services-smma": "services",
  "ai-cold-email-agency": "services",
  "review-to-revenue-content-packs": "services",
  "faq-customer-response-vaults": "services",
  "sop-staff-onboarding-kits": "services",
  "accessibility-content-upgrade-packs": "services",
  "multilingual-local-marketing-kits": "services",
  "estate-sale-probate-cataloguer": "services",
  "insurance-claim-photo-documentation-kit": "services",
  "wedding-speech-vows-studio": "services",
  "restaurant-menu-engineering": "services",
  "cv-linkedin-rescue-service": "services",
  "podcast-ghostwriter-studio": "services",
  "local-business-website-rescue": "services",
  "chatbot-as-a-service": "services",
  "resume-rescue-bots": "services",
  "local-seo-audits": "services",
  "etsy-store-optimiser": "services",
  "twitter-ghostwriter": "services",
  "voiceover-studio": "services",
  "real-estate-listing-rescue": "services",
  "digital-marketing-agency": "services",
  // 116-Track Drop (Feb 4, 2026)
  "ai-sleep-story-asmr-farm": "faceless",
  "ai-colouring-book-publisher": "faceless",
  "ai-educational-worksheets-studio": "faceless",
  "ai-meme-shitpost-studio": "faceless",
  "ai-sales-deck-designer": "services",
  "ai-white-label-content-agency": "services",
  "ai-corporate-rfp-writer": "services",
  "ai-investor-update-ghostwriter": "services",
  "ai-reddit-community-growth-agency": "services",
  "ai-direct-mail-postcard-service": "services",
  "ai-yearbook-class-memory-studio": "commerce",
  "ai-crowdfunding-launch-studio": "services",
  "ai-genealogy-storybook-service": "services",
  "ai-property-investment-newsletter": "services",
  "ai-product-photography-studio": "commerce",

  // Top-10 Fastest-Path Drop (Feb 5, 2026)
  "ai-content-writing": "services",
  "ai-consulting": "services",
  "no-code-app-builder": "commerce",

  // Trending Money-Making Drop (Feb 5, 2026)
  "ai-geo-llm-seo-consulting": "services",
  "vibe-coding-microsaas-studio": "commerce",
  "ai-meta-ads-creative-studio": "services",
  "ai-personalised-kids-books": "commerce",
  "ai-video-dubbing-localisation": "services",
  "ai-podcast-shorts-farmer": "services",
  "ai-vacant-land-flip-newsletter": "commerce",
  "ai-glp1-clinic-content-machine": "services",
  "ai-executive-assistant-service": "services",
  "ai-instagram-carousel-farm": "services",

  // AI Training Course Builder Drop (Feb 6, 2026)
  "training-course-business": "services",
  "training-course-business-phase-2": "services",
  "course-distribution-multi-channel": "services",
  // Fast-Monetisation B2B Services Drop (Aug 28, 2026)
  "sop-micro-training-studio": "services",
  "customer-education-academy-studio": "services",
  "trades-handover-pack-studio": "services",
  "supplier-subcontractor-onboarding-studio": "services",
  "grassroots-sponsorship-revenue-studio": "services",
  "trades-office-ai-training": "services",
  "trades-quote-conversion-training": "services",
  "automotive-service-advisor-training": "services",
  "salon-booking-conversion-training": "services",
  "complaint-handling-micro-training": "services",
  "estate-letting-agent-ai-training": "services",
  "trades-maintenance-plan-launch": "services",
  "lost-customer-reactivation-studio": "services",
  "competitor-intelligence-local-business": "services",
  "commercial-cleaning-quality-reporting": "services",

  // Subscriptions — recurring member / sub models.
  newsletter: "subs",
  "community-membership": "subs",
  "personal-brand-os": "subs",
  "newsletter-business": "subs",
  "tender-grant-alert-subscription": "subs",
  "planning-application-lead-digests": "subs",
  "compliance-calendar-evidence-packs": "subs",
  "local-news-substack-underserved": "subs",
  "airbnb-listing-rescue-optimiser": "subs",
  "personal-chef-meal-plan-service": "subs",
  "micro-community-builder": "subs",

  // E-commerce & Digital — selling stuff (digital or physical) online.
  "ebook-writing": "commerce",
  "print-on-demand": "commerce",
  "digital-products": "commerce",
  dropshipping: "commerce",
  "ecommerce-shopify": "commerce",
  "resale-flipping": "commerce",
  "domain-flipping": "commerce",
  "digital-downloads": "commerce",
  "ai-niche-data-vaults": "commerce",
  "ai-workflow-marketplace": "commerce",
  "3d-printing-file-licensing": "commerce",
  "game-asset-royalty-studio": "commerce",
  "resale-listing-systems": "commerce",
  "founders-special-fast-money": "commerce",
  "tattoo-design-49": "commerce",
  "digital-legacy-executor-handover-planner-kits": "commerce",
  "senior-pet-care-memory-planner-packs": "commerce",
  "ai-wedding-planning-planner-packs": "commerce",
  "uk-landlord-compliance-planner-kits": "commerce",
  "neurodivergent-friendly-study-planner-packs": "commerce",
  "side-hustle-tax-self-assessment-prep-kits": "commerce",
  "household-bill-audit-renegotiation-script-packs": "commerce",
  "cash-stuffing-sinking-fund-budget-template-studio": "commerce",
  "lost-pension-tracer-consolidation-checklist-kits": "commerce",
  "micro-business-grant-funding-finder-newsletter": "commerce",
  "bank-switch-cashback-bonus-tracker-newsletter": "commerce",
  "dividend-income-portfolio-tracker-spreadsheet-studio": "commerce",
  "community-venue-hire-booking-ops-kits": "commerce",
  "white-label-destination-guest-guides": "commerce",
  "sports-club-committee-handover-volunteer-kits": "commerce",
  "tenant-welcome-move-in-guide-packs-letting-agents": "commerce",
  "charity-trustee-onboarding-governance-pack-studio": "commerce",
  "independent-pharmacy-service-promotion-kits": "commerce",
  "service-business-price-increase-communication-kits": "commerce",
  "livery-yard-equestrian-ops-kits": "commerce",
  "van-fleet-driver-handbook-grey-fleet-policy-kits": "commerce",
  "market-craft-fair-stallholder-sales-kits": "commerce",
  "small-business-sale-readiness-data-room-kits": "commerce",

  // Freelance & Growth — personal skill selling, career, coaching, general growth.
  "affiliate-marketing": "growth",
  "blogging-seo": "growth",
  "online-courses": "growth",
  "freelance-automation": "growth",
  "ai-music-generation": "growth",
  "smma-agency": "growth",
  "side-hustle-ideas": "growth",
  "email-marketing": "growth",
  "sales-offers": "growth",
  "brand-deals": "growth",
  "brrr-property": "growth",
  recruit: "growth",

  // Templates & Systems — templates, kits, SaaS, semi-passive physical systems.
  "social-media-templates": "systems",
  "saas-ideas": "systems",
  "landing-page": "systems",
  "ai-notion-template-studio": "systems",
  "ai-saas-micro-app": "systems",
  "ai-vending-route-ops": "systems",
  "equipment-tool-hire-systems": "systems",
  "local-digital-signage-networks": "systems",
  "parcel-locker-collection-hosting": "systems",
  "self-service-pet-wash-stations": "systems",
  "voice-note-to-digital-product": "systems",

  // Operator Skills — cross-cutting strategy / brand / productivity / research / power prompts.
  "operator-business-strategy": "operator",
  "operator-branding-positioning": "operator",
  "operator-productivity-systems": "operator",
  "operator-market-research-validation": "operator",
  "operator-power-prompts": "operator",
};

export function categoryOf(slug) {
  return TRACK_CATEGORY_MAP[slug] || "growth";
}
